import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faBatteryHalf,
    faBolt,
    faClock,
    faHistory,
    faLock,
    faLockOpen,
    faMapLocationDot,
    faSatellite,
    faShieldAlt,
    faSignal,
    faTags,
    faTimes,
    faUnlock,
} from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, getLoggedInUser, showToast} from "../../utils/utility";
import {hasPermission} from "../../utils/rolePermissions";
import LabelInput from "../AssetManagement/LabelInput";
import LiveLocationModal from "./LiveLocationModal";
import PasscodeVerificationModal from "./PasscodeVerificationModal";
import RouteHistoryModal from './RouteHistoryModal';
import "../../styles/components/dynamic-table.css";

export default function DeviceDetailsPanel({
  selectedDevice,
  assetName,
  assetType,
  vehicleType,
  protocol,
  assetId,
  details,
  actions,
  onClose,
  passcodeFeature
}) {

  const navigate = useNavigate();
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [activePasscodeData, setActivePasscodeData] = useState(null);
  const [isFetchingPasscode, setIsFetchingPasscode] = useState(false);
  const [showLiveMap, setShowLiveMap] = useState(false);
  const [showRouteHistory, setShowRouteHistory] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [assetLabels, setAssetLabels] = useState([]);

  useEffect(() => {
    setShowPasscodeModal(false);
    setActivePasscodeData(null);
    setShowRouteHistory(false);
    setIsClosing(false);
  }, [selectedDevice]);

  // Point 2 — load the selected asset's labels so they can be managed from the dashboard.
  useEffect(() => {
    if (!assetId) {
      setAssetLabels([]);
      return;
    }
    const loadLabels = async () => {
      try {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-labels/asset/${assetId}`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const resp = await axios.get(url);
        if (resp.status === 200) setAssetLabels(resp.data.responseData || []);
      } catch {
        setAssetLabels([]);
      }
    };
    loadLabels();
  }, [assetId]);

  /* =======================
       GLOBAL SECTION FLAGS
  ======================== */

  const isBtLock = assetType === "BT_LOCK";
  const isGPS_LOCK = assetType === "GPS_LOCK";
  const isMobiTrack = assetType === "MOBI_TRACK";
  const isGt06 = assetType === "GT06_TRACKER";
  // FT920 (JT808) trackers — e.g. pet trackers — have no meaningful ignition state.
  const isFt920 = (protocol || "").toUpperCase() === "FT920";

  const lockCardLabel = isBtLock ? "Lock Status" : "Lock Rope";

  const hardwareGridClass = (isBtLock || isGt06)
    ? "metrics-grid single-center"
    : "metrics-grid";

  const telemetryGridClass = isMobiTrack
    ? "metrics-grid single-center"
    : "metrics-grid";

  const SECTION_FLAGS = {
    SHOW_CONTROL_ACTIONS: !isBtLock,
    SHOW_BACK_COVER: !isBtLock && !isMobiTrack && !isGt06,
    SHOW_LOCK_ROPE: !isMobiTrack && !isGt06,
    SHOW_SATELLITES: !isBtLock && !isMobiTrack,
    SHOW_SIGNAL: !isBtLock && !isMobiTrack,
    SHOW_BATTERY: !isMobiTrack && !isGt06,
    SHOW_LAST_PING: true,
    SHOW_LOCATION: true,
    SHOW_ALERTS: !isMobiTrack && !isGt06,
  };

  /* =======================
       EARLY RETURN
  ======================== */

  if (!selectedDevice || !details) return null;

  /* =======================
       DATA
  ======================== */

  const {
    deviceStatus,
    lockStatus,
    backCoverStatus,
    noOfSatellite,
    gsmSignalQuality,
    dateTime,
    batteryStatus,
    deviceLat,
    deviceLong,
    alerts = {},
    isVisible,
    actionMessage,
    gt06AccOn,
    gt06Speed,
    gt06VoltageLevel,
    gt06GsmSignal,
  } = details;

  const isGt06Online = isGt06 && deviceStatus === Constants.DEVICE_ONLINE;

  const user = getLoggedInUser();

  /* =======================
       HELPERS
  ======================== */

  const getActiveAlerts = () => {
    const active = [];
    if (alerts.lowBatteryAlert) active.push({ label: "Low Battery", icon: faBatteryHalf });
    if (alerts.backCoverOpenAlert) active.push({ label: "Back Cover Open", icon: faUnlock });
    if (alerts.vibrationAlert) active.push({ label: "Vibration Detected", icon: faSignal });
    if (alerts.lockRopeTamperAlert) active.push({ label: "Rope Tamper", icon: faLockOpen });
    if (alerts.unlockingAlert) active.push({ label: "Unauthorized Unlock", icon: faUnlock });
    if (alerts.wrongPasswordAlert) active.push({ label: "Wrong Password", icon: faShieldAlt });
    if (alerts.unauthorizedRfidAlert) active.push({ label: "Unauthorized RFID", icon: faSignal });
    return active;
  };

  const activeAlertList = getActiveAlerts();

  const MapLink = ({ latitude, longitude }) => {
    if (!latitude || !longitude) return "Location unavailable";
    return (
      <a
        href={`https://www.google.com/maps?q=${latitude},${longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="map-link"
      >
        View on Google Maps
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </a>
    );
  };

  /* =======================
       PASSCODE LOGIC (GPS_LOCK)
  ======================== */

  const fetchPasscodeFromServer = async () => {
    if (!assetId) {
      showToast("Asset ID missing", { type: "error" });
      return null;
    }
    setIsFetchingPasscode(true);
    try {
      const clientId = getClientIdOfUser();
      const userId = user.userId;
      const endPoint = `${Constants.API_V1}/${clientId}/passcodes`;
      const url = `${Constants.BASE_URL}${endPoint}?assetId=${assetId}&userId=${userId}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const response = await axios.get(url);
      const passcodeData = response?.data?.responseData;
      if (!passcodeData || !passcodeData.passcode) {
        showToast("No active passcode found. Please generate a passcode first.", { type: "warning" });
        return null;
      }
      return passcodeData;
    } catch (error) {
      console.error("Fetch passcode error:", error);
      showToast("No active passcode available. Contact administrator.", { type: "error" });
      return null;
    } finally {
      setIsFetchingPasscode(false);
    }
  };

  const handleUnlockClick = async () => {
    if (isGPS_LOCK && passcodeFeature) {
      const passcodeData = await fetchPasscodeFromServer();
      if (passcodeData) {
        setActivePasscodeData(passcodeData);
        setShowPasscodeModal(true);
      }
    } else {
      actions.showConfirmation("(P43,XXXXXX)");
    }
  };

  const handlePasscodeVerified = async () => {
    if (!activePasscodeData?.passcode) {
      showToast("Invalid passcode session. Please retry.", { type: "error" });
      return;
    }
    try {
      const clientId = getClientIdOfUser();
      const userId = user.userId;
      const payload = { assetId, userId, passcode: activePasscodeData.passcode };
      const endPoint = `${Constants.API_V1}/${clientId}/passcodes/verify`;
      const response = await axios.post(
        Constants.BASE_URL + endPoint,
        payload,
        { headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` } }
      );
      const result = response?.data?.responseData;
      if (!result?.success) {
        showToast(result?.message || "Passcode verification failed", { type: "error" });
        return;
      }
      showToast("Passcode verified. Unlocking device...", { type: "success" });
      setShowPasscodeModal(false);
      setActivePasscodeData(null);
      actions.showConfirmation("(P43,XXXXXX)");
    } catch (error) {
      console.error("Verify passcode error:", error);
      showToast("Verification failed. Please try again.", { type: "error" });
    }
  };

  const handleViewHistory = () => setShowRouteHistory(true);

  const handleClose = () => setIsClosing(true);

  /* =======================
       JSX
  ======================== */

  return (
    <div
      className={`grid-div col-right-details${isClosing ? " panel-closing" : ""}`}
      onAnimationEnd={() => { if (isClosing) onClose(); }}
    >
      {/* CLOSE */}
      <button className="details-close-btn" onClick={handleClose} aria-label="Close details panel">
        <FontAwesomeIcon icon={faTimes} />
      </button>

      {/* HEADER */}
      <div className="device-header-card">
        <h2 className="device-title-large">{assetName || "Select Device"}</h2>
        <div className="device-id-sub">{selectedDevice}</div>
        <div style={{marginTop: "8px", display: "flex", justifyContent: "center", alignItems: "center"}}>
          <span
            className={`status-badge ${
              deviceStatus === Constants.DEVICE_ONLINE ? "badge-online" : "badge-offline"
            }`}
          >
            {deviceStatus || "Unknown"}
          </span>
        </div>
      </div>

      {/* LABELS (point 2) — manage labels for the assigned asset from the dashboard */}
      {assetId && (
        <div className="detail-section">
          <div className="detail-header">
            <FontAwesomeIcon icon={faTags} /> Labels
          </div>
          <div style={{display: "flex", justifyContent: "center", padding: "4px 0"}}>
            <LabelInput
              assetId={assetId}
              labels={assetLabels}
              onLabelsChange={(_id, newLabels) => setAssetLabels(newLabels)}
            />
          </div>
        </div>
      )}

      {/* CONTROL ACTIONS */}
      {SECTION_FLAGS.SHOW_CONTROL_ACTIONS && (
        <div className="detail-section">
          <div className="detail-header">Control Actions</div>

          {(isMobiTrack || isGt06) ? (
            <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
              <button
                className="action-btn-large secondary"
                onClick={() => setShowLiveMap(true)}
                disabled={!deviceLat || !deviceLong}
              >
                <FontAwesomeIcon icon={faMapLocationDot} />
                {/* Match the Android wording per device type: Track Point → "Live Tracking",
                    GT06 tracker → "View Live Location". */}
                {isMobiTrack ? "Live Tracking" : "View Live Location"}
              </button>
              {isMobiTrack && (
                <button
                  className="action-btn-large secondary alt"
                  onClick={handleViewHistory}
                >
                  <FontAwesomeIcon icon={faHistory} />
                  {/* Matches Android's Track Point "View Route" button. */}
                  View Route
                </button>
              )}
            </div>
          ) : (
            <>
              <button
                className="action-btn-large"
                disabled={
                  !selectedDevice ||
                  lockStatus === Constants.LOCK_OPEN ||
                  deviceStatus !== Constants.DEVICE_ONLINE ||
                  isFetchingPasscode
                }
                onClick={handleUnlockClick}
              >
                <FontAwesomeIcon icon={faUnlock} />
                Unlock Asset
              </button>
              {/* Schedule a future unlock for THIS lock — GPS Lock only (BT locks are
                  Bluetooth-only and can't be scheduled server-side). Carries the device
                  through so the scheduler opens pre-selected on it. */}
              {isGPS_LOCK && hasPermission("sidebar_scheduledUnlocks") && (
                <button
                  className="action-btn-large secondary alt"
                  style={{marginTop: "10px"}}
                  disabled={!selectedDevice}
                  onClick={() =>
                    navigate("/scheduled-unlocks", {
                      state: {presetAssetUnitId: selectedDevice, presetAssetName: assetName},
                    })
                  }
                >
                  <FontAwesomeIcon icon={faClock} />
                  Schedule Unlock
                </button>
              )}
              {isVisible && <div className="action-feedback">{actionMessage}</div>}
            </>
          )}
        </div>
      )}

      {/* LOCATION — map link only (lat/long removed; the link covers it) */}
      {SECTION_FLAGS.SHOW_LOCATION && (
        <div className="detail-section">
          <div className="detail-header">Location</div>
          <div className="location-map-card">
            <FontAwesomeIcon icon={faMapLocationDot} className="location-map-icon" />
            <MapLink latitude={deviceLat} longitude={deviceLong} />
          </div>
        </div>
      )}

      {/* IGNITION & MOVEMENT (GT06 only) */}
      {isGt06 && (
        <div className="detail-section">
          <div className="detail-header">Ignition &amp; Movement</div>
          <div className="metrics-grid">
            <div className="metric-box">
              <FontAwesomeIcon icon={faBolt} className="metric-icon" />
              <span className="metric-label">Ignition</span>
              {isFt920 ? (
                <span className="metric-value" style={{color: "#9ca3af"}}>N/A</span>
              ) : (
                <span
                  className="metric-value"
                  style={{
                    color: isGt06Online && gt06AccOn === true
                      ? "#16a34a"
                      : (!isGt06Online || gt06AccOn === false)
                      ? "#dc2626"
                      : undefined,
                  }}
                >
                  {isGt06Online && gt06AccOn === true ? "ON" : "OFF"}
                </span>
              )}
            </div>
            <div className="metric-box">
              <FontAwesomeIcon icon={faSignal} className="metric-icon" />
              <span className="metric-label">Speed</span>
              <span className="metric-value">
                {!isGt06Online || gt06Speed == null ? "-" : `${gt06Speed} km/h`}
              </span>
            </div>
            <div className="metric-box">
              <FontAwesomeIcon icon={faBatteryHalf} className="metric-icon" />
              <span className="metric-label">Voltage</span>
              <span className="metric-value">
                {!isGt06Online || gt06VoltageLevel == null ? "-" : String(gt06VoltageLevel)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* HARDWARE STATUS */}
      {!isMobiTrack && !isGt06 && (
        <div className="detail-section">
          <div className="detail-header">Hardware Status</div>
          <div className={hardwareGridClass}>
            {SECTION_FLAGS.SHOW_BACK_COVER && (
              <div className="metric-box">
                <span className="metric-label">Back Cover</span>
                <span
                  className="metric-value"
                  style={{color: backCoverStatus === Constants.BACK_COVER_CLOSE ? "green" : "red"}}
                >
                  {backCoverStatus || "-"}
                </span>
              </div>
            )}
            {SECTION_FLAGS.SHOW_LOCK_ROPE && (
              <div className="metric-box">
                <span className="metric-label">{lockCardLabel}</span>
                <span className="metric-value">{lockStatus || "-"}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ALERTS */}
      {SECTION_FLAGS.SHOW_ALERTS && (
        <div className="detail-section">
          <div className="detail-header">System Alerts</div>
          <div className="alerts-container">
            {activeAlertList.length === 0 ? (
              <div className="alert-chip success">
                <FontAwesomeIcon icon={faLock} /> System Healthy
              </div>
            ) : (
              activeAlertList.map((alert, idx) => (
                <div key={idx} className="alert-chip danger">
                  <FontAwesomeIcon icon={alert.icon} /> {alert.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* LIVE TELEMETRY */}
      <div className="detail-section">
        <div className="detail-header">Live Telemetry</div>
        <div className={telemetryGridClass}>
          {SECTION_FLAGS.SHOW_SATELLITES && (
            <div className="metric-box">
              <FontAwesomeIcon icon={faSatellite} className="metric-icon" />
              <span className="metric-label">Satellites</span>
              <span className="metric-value">
                {isGt06 ? (details.gt06Satellites != null ? details.gt06Satellites : "-") : (noOfSatellite || "-")}
              </span>
            </div>
          )}
          {SECTION_FLAGS.SHOW_SIGNAL && (
            <div className="metric-box">
              <FontAwesomeIcon icon={faSignal} className="metric-icon" />
              <span className="metric-label">Signal</span>
              <span className="metric-value">
                {isGt06 ? (gt06GsmSignal != null ? String(gt06GsmSignal) : "-") : (gsmSignalQuality || "-")}
              </span>
            </div>
          )}
          {SECTION_FLAGS.SHOW_BATTERY && (
            <div className="metric-box">
              <FontAwesomeIcon icon={faBatteryHalf} className="metric-icon" />
              <span className="metric-label">Battery</span>
              <span className="metric-value">{batteryStatus || "-"}</span>
            </div>
          )}
          {SECTION_FLAGS.SHOW_LAST_PING && (
            <div className="metric-box">
              <FontAwesomeIcon icon={faClock} className="metric-icon" />
              <span className="metric-label">Last Ping</span>
              <span className="metric-value" style={{fontSize: "0.75rem"}}>
                {dateTime?.split(" ")[1] || "-"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* PASSCODE MODAL */}
      {showPasscodeModal && activePasscodeData && (
        <PasscodeVerificationModal
          serverPasscode={activePasscodeData.passcode}
          assetName={assetName}
          onVerified={handlePasscodeVerified}
          onClose={() => { setShowPasscodeModal(false); setActivePasscodeData(null); }}
        />
      )}

      {/* LIVE LOCATION MODAL */}
      {showLiveMap && (
        <LiveLocationModal
          assetUnitId={selectedDevice}
          latitude={deviceLat}
          longitude={deviceLong}
          assetType={assetType}
          vehicleType={vehicleType}
          assetName={assetName}
          deviceStatus={deviceStatus}
          initialSpeedKmph={gt06Speed}
          onClose={() => setShowLiveMap(false)}
        />
      )}

      {/* ROUTE HISTORY MODAL */}
      {showRouteHistory && (
        <RouteHistoryModal
          assetUnitId={selectedDevice}
          assetType={assetType}
          vehicleType={vehicleType}
          assetName={assetName}
          baseUrl={Constants.BASE_URL}
          authToken={getAuthToken()}
          onClose={() => setShowRouteHistory(false)}
        />
      )}
    </div>
  );
}
